import { type SuiteContextV1, type SuiteProductV1, orderSuiteProducts } from "@fulcrum5/suite-contracts";
import { Grip } from "lucide-react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAnchoredPopover } from "./useAnchoredPopover.ts";

export interface SuiteLauncherProps {
  readonly context: SuiteContextV1;
  readonly onSwitchOrganization: (workosOrganizationId: string) => Promise<void>;
  readonly busy?: boolean;
  readonly className?: string;
}

function joinClassNames(...classNames: Array<string | undefined>): string {
  return classNames.filter((className): className is string => Boolean(className)).join(" ");
}

function nextFocusIndex(key: string, currentIndex: number, count: number): number | null {
  if (count === 0) return null;
  if (key === "Home") return 0;
  if (key === "End") return count - 1;
  if (key === "ArrowRight" || key === "ArrowDown") return (currentIndex + 1) % count;
  if (key === "ArrowLeft" || key === "ArrowUp") return (currentIndex - 1 + count) % count;
  return null;
}

/**
 * Minimal product marks — one family, only the letter changes:
 *   navy tile · white monogram · gold fulcrum triangle (Platform style for all).
 *   Platform=5 · Source=S · Codify=C · Function=F · Radius=R
 */
const PRODUCT_LETTER: Record<string, string> = {
  platform: "5",
  codify: "C",
  source: "S",
  radius: "R",
  function: "F",
};
const TILE_R = 8;
const FULCRUM = "20,27.5 15,35 25,35";

function ProductIconMark({ productKey, name }: { productKey: string; name: string }) {
  const letter = PRODUCT_LETTER[productKey] ?? name.slice(0, 1).toUpperCase();
  return (
    <span className="f5-suite-launcher__app-icon" aria-hidden="true" data-product={productKey}>
      <svg viewBox="0 0 40 40" width="36" height="36" focusable="false">
        <rect width="40" height="40" rx={TILE_R} ry={TILE_R} fill="#1A3A6B" />
        <text
          x="20"
          y="22.2"
          textAnchor="middle"
          fill="#FFFFFF"
          fontSize="16.25"
          fontWeight={700}
          fontFamily="Outfit, Inter, ui-sans-serif, system-ui, sans-serif"
          letterSpacing="-0.04em"
        >
          {letter}
        </text>
        <polygon points={FULCRUM} fill="#D4A82A" />
      </svg>
    </span>
  );
}

function ProductLaunchTile({
  product,
  active,
  index,
  onKeyDown,
  registerAnchor,
}: {
  readonly product: SuiteProductV1;
  readonly active: boolean;
  readonly index: number;
  readonly onKeyDown: (event: React.KeyboardEvent<HTMLAnchorElement>, index: number) => void;
  readonly registerAnchor: (index: number, element: HTMLAnchorElement | null) => void;
}) {
  // M365-style app cell: icon + name side-by-side. Description lives in title /
  // sr-only so the grid stays compact (not a stack of description cards).
  if (!product.available) {
    return (
      <div
        aria-current={active ? "page" : undefined}
        aria-disabled="true"
        className={joinClassNames(
          "f5-suite-launcher__app",
          "f5-suite-launcher__app--unavailable",
          active ? "f5-suite-launcher__app--active" : undefined,
        )}
        data-suite-product={product.key}
        title={product.unavailableReason ?? product.description}
      >
        <ProductIconMark productKey={product.key} name={product.name} />
        <span className="f5-suite-launcher__app-copy">
          <span className="f5-suite-launcher__app-name">{product.name}</span>
          <span className="f5-suite-launcher__app-description">{product.description}</span>
          {product.unavailableReason ? (
            <span className="f5-suite-launcher__app-reason">{product.unavailableReason}</span>
          ) : null}
        </span>
      </div>
    );
  }

  return (
    <a
      ref={(element) => registerAnchor(index, element)}
      aria-current={active ? "page" : undefined}
      className={joinClassNames(
        "f5-suite-launcher__app",
        active ? "f5-suite-launcher__app--active" : undefined,
      )}
      data-suite-product={product.key}
      href={product.launchUrl}
      title={product.description}
      onKeyDown={(event) => onKeyDown(event, index)}
    >
      <ProductIconMark productKey={product.key} name={product.name} />
      <span className="f5-suite-launcher__app-copy">
        <span className="f5-suite-launcher__app-name">{product.name}</span>
        <span className="f5-suite-launcher__app-description">{product.description}</span>
      </span>
    </a>
  );
}

/**
 * A host-neutral launcher for the five Fulcrum5 applications. It intentionally
 * navigates with ordinary anchors, keeping product frontends independent.
 */
export function SuiteLauncher({
  context,
  onSwitchOrganization,
  busy = false,
  className,
}: SuiteLauncherProps) {
  const [open, setOpen] = useState(false);
  const [organizationChoicesOpen, setOrganizationChoicesOpen] = useState(false);
  const [switchingOrganization, setSwitchingOrganization] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const availableAppRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const products = useMemo(() => orderSuiteProducts(context.products), [context.products]);
  const availableProducts = useMemo(() => products.filter((product) => product.available), [products]);
  const platformProduct = useMemo(() => products.find((product) => product.key === "platform"), [products]);
  const canSwitchOrganization = context.activeOrganization !== null && context.organizations.length > 1;
  const isBusy = busy || switchingOrganization;

  const closeLauncher = useCallback(() => {
    setOrganizationChoicesOpen(false);
    setOpen(false);
    triggerRef.current?.focus();
  }, []);
  const { style: popoverStyle } = useAnchoredPopover({
    open,
    anchorRef: triggerRef,
    panelRef,
    onRequestClose: closeLauncher,
  });

  useLayoutEffect(() => {
    if (!open) return;
    availableAppRefs.current[0]?.focus();
  }, [open, availableProducts]);

  const registerAnchor = useCallback((index: number, element: HTMLAnchorElement | null) => {
    availableAppRefs.current[index] = element;
  }, []);

  const moveAppFocus = useCallback((event: React.KeyboardEvent<HTMLAnchorElement>, currentIndex: number) => {
    const nextIndex = nextFocusIndex(event.key, currentIndex, availableProducts.length);
    if (nextIndex === null) return;
    event.preventDefault();
    availableAppRefs.current[nextIndex]?.focus();
  }, [availableProducts.length]);

  const switchOrganization = useCallback(async (workosOrganizationId: string) => {
    if (isBusy || context.activeOrganization?.workosOrganizationId === workosOrganizationId) return;
    setOrganizationChoicesOpen(false);
    setSwitchingOrganization(true);
    try {
      await onSwitchOrganization(workosOrganizationId);
    } catch {
      // A successful broker call navigates away. Only an unsuccessful one
      // returns to this component, where the caller can try again.
      setSwitchingOrganization(false);
    }
  }, [context.activeOrganization?.workosOrganizationId, isBusy, onSwitchOrganization]);

  const panel = open && typeof document !== "undefined"
    ? createPortal(
      <section
        ref={panelRef}
        aria-busy={isBusy}
        aria-label="Fulcrum5 apps"
        className="f5-suite-root f5-suite-launcher__panel"
        role="dialog"
        style={popoverStyle}
      >
        <div className="f5-suite-launcher__apps" aria-label="Fulcrum5 applications">
          {products.map((product) => {
            const availableIndex = availableProducts.findIndex((availableProduct) => availableProduct.key === product.key);
            return (
              <ProductLaunchTile
                key={product.key}
                active={product.key === context.currentProduct}
                index={availableIndex}
                product={product}
                onKeyDown={moveAppFocus}
                registerAnchor={registerAnchor}
              />
            );
          })}
        </div>

        <div className="f5-suite-launcher__organization">
          <span className="f5-suite-launcher__organization-label">Organization</span>
          <span className="f5-suite-launcher__organization-name">
            {context.activeOrganization?.name ?? "Platform administration"}
          </span>
          {context.hasMoreOrganizations && platformProduct !== undefined && (
            <div className="f5-suite-launcher__organization-overflow">
              <span className="f5-suite-launcher__organization-overflow-notice" role="status">
                More organizations are available.
              </span>
              <a
                aria-label="View all organizations in Platform"
                className="f5-suite-launcher__organization-overflow-link"
                data-suite-product="platform"
                href={platformProduct.launchUrl}
              >
                View all in Platform
              </a>
            </div>
          )}
          {canSwitchOrganization && (
            <>
              <button
                type="button"
                aria-expanded={organizationChoicesOpen}
                aria-controls="f5-suite-organization-choices"
                className="f5-suite-launcher__organization-toggle"
                disabled={isBusy}
                onClick={() => setOrganizationChoicesOpen((current) => !current)}
              >
                Switch organization
              </button>
              {organizationChoicesOpen && (
                <div
                  id="f5-suite-organization-choices"
                  aria-label="Choose organization"
                  className="f5-suite-launcher__organization-choices"
                  role="group"
                >
                  {context.organizations
                    .filter((organization) => organization.workosOrganizationId !== context.activeOrganization?.workosOrganizationId)
                    .map((organization) => (
                      <button
                        key={organization.workosOrganizationId}
                        type="button"
                        className="f5-suite-launcher__organization-choice"
                        disabled={isBusy}
                        onClick={() => void switchOrganization(organization.workosOrganizationId)}
                      >
                        {organization.name}
                      </button>
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>,
      document.body,
    )
    : null;

  return (
    <div className={joinClassNames("f5-suite-root", "f5-suite-launcher", className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="App launcher"
        className="f5-suite-launcher__trigger"
        disabled={isBusy}
        onClick={() => open ? closeLauncher() : setOpen(true)}
        title="App launcher"
      >
        <Grip aria-hidden="true" size={18} />
      </button>
      {panel}
    </div>
  );
}
